import { api } from "@/lib/api";

// (mantém em sincronia com packages/db/src/schema/knowledge_bases.ts)
export type KnowledgeBaseType =
  | "onboarding"
  | "product"
  | "material"
  | "competitor"
  | "reference"
  | "custom";

export type KnowledgeBaseSourceKind =
  | "manual"
  | "url"
  | "pdf"
  | "csv"
  | "video"
  | "seeded";

export type KnowledgeBaseScope = "always" | "task-relevant";
export type KnowledgeBaseStatus = "ready" | "processing" | "failed";

export interface ProductPersona {
  nomeFicticio: string;
  perfilDemografico?: string;
  dores?: string[];
  desejos?: string[];
  objecoes?: string[];
}

export interface ProductResultado {
  tipo: "proprio" | "aluno";
  descricao: string;
  metrica?: string;
}

export interface ProductMeta {
  nome?: string;
  tipo?: string;
  preco?: string;
  moeda?: string;
  formasPagamento?: string[];
  descricao?: string;
  entregaveis?: string;
  formatoEntrega?: string;
  garantia?: string;
  tempoAcesso?: string;
  mecanismoUnicoSolucao?: string;
  bonus?: string[];
  beneficios?: {
    funcionais?: string[];
    emocionais?: string[];
    dimensionais?: string[];
  };
  promessa?: string;
  urgencia?: string;
  publicoAlvoOverride?: string;
  tomVozOverride?: string[];
  personas?: ProductPersona[];
  concorrentes?: string[];
  resultados?: ProductResultado[];
  notas?: string;
  icp?: string;
}

export interface OwnInstagramMeta {
  handle: string;
  scrapedAt: string;
  profile?: unknown;
  topPosts?: Array<{
    mediaType: "image" | "video";
    url: string;
    thumbUrl?: string;
    caption?: string;
    likes?: number;
    comments?: number;
    sourceUrl?: string;
    timestamp?: string;
  }>;
  analysis: string;
}

/** Espelha CompetitorMeta do servidor (server/src/services/competitor-analyzer.ts).
 *  Vive em knowledge_bases.content_meta.competitor quando type='competitor'. */
export interface CompetitorTopCreative {
  kind: "post" | "ad";
  mediaType: "image" | "video";
  url: string;
  thumbUrl?: string;
  /** Asset permanente do thumb baixado (/api/assets/<id>/content). */
  contentPath?: string;
  caption?: string;
  metricValue?: number;
  metricLabel?: string;
  sourceUrl?: string;
  ctaText?: string;
  ctaUrl?: string;
}

export interface CompetitorAd {
  pageId?: string;
  pageName?: string;
  adArchiveId?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  publisherPlatform?: string[];
  text?: string;
  ctaText?: string;
  ctaUrl?: string;
  imageUrls?: string[];
  videoUrls?: string[];
  thumbUrl?: string;
  snapshotUrl?: string;
  /** Asset permanente do thumb baixado (Meta CDN expira/CORS). UI prioriza. */
  contentPath?: string;
  [k: string]: unknown;
}

export interface CompetitorInstagramProfile {
  username?: string;
  fullName?: string;
  biography?: string;
  externalUrl?: string;
  followersCount?: number;
  followsCount?: number;
  postsCount?: number;
  profilePicUrl?: string;
  profilePicUrlHD?: string;
  /** Asset permanente da foto de perfil (URLs cruas do IG expiram). UI prioriza. */
  profilePicContentPath?: string;
  latestPosts?: Array<{
    type?: string;
    caption?: string;
    likesCount?: number;
    commentsCount?: number;
    url?: string;
    displayUrl?: string;
    timestamp?: string;
    /** Asset permanente do thumb do post (UI prioriza). */
    contentPath?: string;
    [k: string]: unknown;
  }>;
  [k: string]: unknown;
}

/** Análise estruturada da landing page (espelha LandingPageAnalysis do server). */
export interface LandingPageAnalysis {
  promessaPrincipal?: string;
  publicoAlvo?: string;
  tipoOferta?: string;
  formato?: string;
  preco?: string;
  garantia?: string;
  headlinePrincipal?: string;
  provaSocial?: string;
  ctaPrincipal?: string;
  forcas?: string[];
  fraquezas?: string[];
  observacao?: string;
}

export interface CompetitorMeta {
  handle?: string;
  sourceUrl?: string;
  brandQuery?: string;
  scrapedAt: string;
  profile: CompetitorInstagramProfile | null;
  ads: CompetitorAd[];
  landingPage?: {
    url: string;
    title?: string;
    /** Markdown bruto raspado (mantido pra IA do dossiê + debug). */
    markdown: string;
    /** Análise IA da página em campos estruturados (mesmo padrão do KB de
     *  produto). KBs antigas podem ter como string (markdown) — UI faz fallback. */
    analysis?: LandingPageAnalysis | string;
  };
  topCreatives: CompetitorTopCreative[];
  dossier: string;
  costUsd?: number;
}

/** Tipo de referência — cada um tem seu processo de análise + campos. */
export type ReferenceType =
  | "autoridade"  // por que esse @ é referência no que faz
  | "conteudo"    // formatos/hooks/ritmo que funcionam
  | "criativo"    // estilo visual/layout/paleta
  | "sazonal"     // campanha específica (ex: Black Friday) — upload
  | "landing_page" // estrutura de página: anatomia (seções), persuasão, hierarquia — via Firecrawl ou colado
  | "custom";     // livre

/**
 * Dossiê de referência. Campos GENÉRICOS pra qualquer negócio (e-commerce,
 * advogado, médico, prestador, infoproduto) — nada preso a "expert". A IA
 * pré-preenche a partir da fonte (IG/URL/PDF/imagens) ou o user preenche;
 * é editável e vai pro prompt dos agentes como inspiração.
 */
export interface ReferenceMeta {
  referenceType: ReferenceType;
  /** De onde veio: "@handle", URL, ou nome do arquivo. */
  sourceLabel?: string;
  // ── Comuns a todos os tipos ──
  nome?: string;             // quem/o que é a referência
  porQueReferencia?: string; // o insight central
  posicionamento?: string;   // o ângulo/visão de mundo que sustenta a referência
  oQueAplicar?: string;      // a ponte prática: o que extrair (sem copiar)
  tom?: string;              // tom de voz
  hooks?: string[];          // aberturas/ganchos que prendem
  estruturaNarrativa?: string; // gancho → desenvolvimento → CTA (de vídeo)
  bordoes?: string[];        // frases/vocabulário de assinatura (de vídeo)
  // ── Autoridade ──
  porQueAutoridade?: string; // o diagnóstico: o que faz ser ouvido
  diferencial?: string;      // o que distingue dos pares
  pilares?: string[];        // temas centrais
  sinais?: string[];         // provas de credibilidade
  gatilhos?: string[];       // gatilhos mentais/persuasão recorrentes
  // ── Conteúdo ──
  formatos?: string[];       // formatos/estruturas que funcionam
  // ── Criativo ──
  estiloVisual?: string;     // descrição do estilo visual
  paleta?: string[];         // cores (hex ou nome)
  layout?: string;           // composição/diagramação
  elementosRecorrentes?: string[]; // elementos visuais que se repetem
  // ── Sazonal ──
  contexto?: string;         // contexto da campanha (ex: Black Friday)
  oQueFunciona?: string[];   // o que faz esses criativos funcionarem
  // ── Apoio (matéria-prima, não o ouro) ──
  transcricoes?: string[];   // transcrições dos Reels
  /** Biblioteca visual: posts/carrosséis da referência. Vêm da raspagem do
   *  @ (Apify) OU upload do user ("adicionar mais"). Os agentes de arte usam
   *  como gabarito visual. */
  images?: Array<{
    attachmentId: string;       // = asset.id
    contentPath: string | null; // /api/assets/{id}/content
    caption?: string;           // legenda do post (scrape) ou nota do user
    source?: "scrape" | "upload";
  }>;
}

export interface KnowledgeBaseContentMeta {
  onboarding?: {
    nomeEmpresa?: string;
    segmento?: string;
    segmentoOutro?: string;
    slogan?: string;
    historia?: string;
    posicionamento?: string;
    diferencial?: string;
    credenciais?: string[];
    tomVoz?: string[];
    temasProibidos?: string[];
    missao?: string;
    visao?: string;
    valores?: string[];
    /** MIV — Manual de Identidade Visual da marca. Camada base pra agentes
     *  criativos. Produtos podem ter MIV próprio (em briefing.products);
     *  quando há produto selecionado na tarefa, o MIV do produto sobrescreve. */
    miv?: {
      cores?: string[];          // hex array
      tipografia?: string[];     // nomes das fontes (texto)
      /** Arquivos de fonte enviados (título/corpo) — o renderizador usa os bytes
       *  pra a arte sair na tipografia REAL da marca, não no fallback Bricolage. */
      fontFiles?: Array<{ assetId: string; family: string; role: "headline" | "corpo" }>;
      diretrizes?: string;
      // Logos e materiais (decks/PPTs/PDFs/imagens) ficam em
      // briefing_attachments com category='miv-logo' | 'miv'.
    };
    sobreUsuario?: string;
  };
  product?: ProductMeta;
  ownInstagram?: OwnInstagramMeta;
  competitor?: CompetitorMeta;
  /** Referência (inspiração) — dossiê estruturado por tipo. Campos genéricos
   *  pra qualquer negócio (e-commerce, advogado, médico, serviço, infoproduto).
   *  Pré-preenchido por IA (IG/URL/PDF) ou manual; editável; vai pro prompt. */
  reference?: ReferenceMeta;
  source?: {
    originalUrl?: string;
    originalFilename?: string;
    contentType?: string;
    byteSize?: number;
    transcriptDurationSec?: number;
  };
  [key: string]: unknown;
}

export interface KnowledgeBase {
  id: string;
  companyId: string;
  type: KnowledgeBaseType;
  name: string;
  description: string | null;
  sourceKind: KnowledgeBaseSourceKind;
  contentText: string;
  contentMeta: KnowledgeBaseContentMeta;
  triggerKeywords: string[];
  triggerScope: KnowledgeBaseScope;
  status: KnowledgeBaseStatus;
  statusMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateKnowledgeInput {
  type: KnowledgeBaseType;
  name: string;
  description?: string | null;
  sourceKind: KnowledgeBaseSourceKind;
  contentText?: string;
  contentMeta?: KnowledgeBaseContentMeta;
}

export interface UpdateKnowledgeInput {
  name?: string;
  description?: string | null;
  contentText?: string;
  contentMeta?: KnowledgeBaseContentMeta;
}

export const knowledgeApi = {
  list(companyId: string): Promise<KnowledgeBase[]> {
    return api.get(`/companies/${companyId}/knowledge`);
  },
  get(companyId: string, kbId: string): Promise<KnowledgeBase> {
    return api.get(`/companies/${companyId}/knowledge/${kbId}`);
  },
  getOnboarding(companyId: string): Promise<KnowledgeBase | null> {
    return api.get(`/companies/${companyId}/knowledge/by-type/onboarding`);
  },
  create(companyId: string, input: CreateKnowledgeInput): Promise<KnowledgeBase> {
    return api.post(`/companies/${companyId}/knowledge`, input);
  },
  update(companyId: string, kbId: string, input: UpdateKnowledgeInput): Promise<KnowledgeBase> {
    return api.patch(`/companies/${companyId}/knowledge/${kbId}`, input);
  },
  delete(companyId: string, kbId: string): Promise<void> {
    return api.delete(`/companies/${companyId}/knowledge/${kbId}`);
  },
  /**
   * Reset de onboarding (ferramenta de QA — só funciona em ambiente de dev).
   * Apaga a KB de onboarding pra forçar o wizard de "primeiros passos" a
   * reaparecer, permitindo testar o fluxo de primeiro acesso de novo.
   */
  resetOnboarding(companyId: string): Promise<{ reset: boolean }> {
    return api.delete(`/companies/${companyId}/knowledge/by-type/onboarding`);
  },
  reanalyze(companyId: string, kbId: string): Promise<KnowledgeBase> {
    return api.post(`/companies/${companyId}/knowledge/${kbId}/reanalyze`, {});
  },
  /**
   * Analisa o próprio Instagram do cliente (mesmo pipeline do concorrente,
   * porém com prompt focado em o que já funciona pra essa marca). Resultado
   * enriquece a KB de onboarding (não cria KB nova).
   */
  analyzeOwnInstagram(
    companyId: string,
    handle: string,
  ): Promise<{
    kb: KnowledgeBase | null;
    handle: string;
    topPosts: Array<{
      mediaType: "image" | "video";
      url: string;
      thumbUrl?: string;
      caption?: string;
      likes?: number;
      comments?: number;
      sourceUrl?: string;
      timestamp?: string;
    }>;
    profile: unknown;
  }> {
    return api.post(`/companies/${companyId}/knowledge/own-instagram`, { handle });
  },
  /**
   * Raspa o IG do cliente e extrai os campos do onboarding pra pré-preencher
   * o wizard. NÃO persiste — user valida/corrige antes de salvar. Retorna
   * também o blob ownInstagram (análise textual + topPosts) do MESMO scrape
   * — o wizard guarda e persiste no save, sem re-scrape.
   */
  analyzeOnboardingFromInstagram(
    companyId: string,
    handle: string,
  ): Promise<{
    handle: string;
    fields: {
      nomeEmpresa: string;
      segmento: string;
      historia: string;
      posicionamento: string;
      diferencial: string;
      credenciais: string[];
      tomVoz: string[];
      temasProibidos: string[];
      missao: string;
      visao: string;
      valores: string[];
      publicoAlvo: string;
      observacoes: string;
    };
    instagramMeta: OwnInstagramMeta;
    profile: {
      username?: string;
      fullName?: string;
      followersCount?: number;
      postsCount?: number;
    };
    costUsd: number;
  }> {
    return api.post(
      `/companies/${companyId}/onboarding/analyze-instagram`,
      { handle },
    );
  },
  /**
   * Cria uma KB de concorrente analisando o @handle do Instagram.
   * Backend roda pipeline Apify (IG profile + Meta Ads Library) + Firecrawl
   * (LP se houver) + Claude Haiku escreve dossiê personalizado.
   * Bloqueante, leva 60-120s.
   */
  analyzeCompetitor(companyId: string, handle: string): Promise<KnowledgeBase> {
    return api.post(`/companies/${companyId}/knowledge/competitor`, {
      source: "instagram",
      handle,
    });
  },
  /**
   * Cria uma KB de concorrente analisando uma URL (site ou landing page).
   * Backend faz Firecrawl scrape (obrigatório) + Claude Haiku monta dossiê
   * focado em posicionamento, oferta, copy e prova social do site. Útil
   * pra concorrentes B2B/SaaS que não têm presença forte no Instagram.
   * Bloqueante, leva 30-60s (mais rápido que Instagram porque não roda
   * Apify nem Ads Library).
   */
  analyzeCompetitorByUrl(companyId: string, url: string): Promise<KnowledgeBase> {
    return api.post(`/companies/${companyId}/knowledge/competitor`, {
      source: "url",
      url,
    });
  },
  /**
   * Cria uma KB de referência (inspiração) a partir de um @ do Instagram.
   * Backend raspa o perfil (Apify), baixa top posts pra biblioteca visual e
   * a IA monta o dossiê do tipo escolhido. Bloqueante, ~1-2 min.
   */
  analyzeReferenceInstagram(
    companyId: string,
    input: { handle: string; referenceType: ReferenceType; name?: string },
  ): Promise<KnowledgeBase> {
    return api.post(
      `/companies/${companyId}/knowledge/reference/analyze-instagram`,
      input,
    );
  },
  /** "Adicionar link": cria uma referência a partir de UM post específico (URL). */
  analyzeReferencePost(
    companyId: string,
    input: { postUrl: string; referenceType: ReferenceType; name?: string },
  ): Promise<KnowledgeBase> {
    return api.post(
      `/companies/${companyId}/knowledge/reference/analyze-post`,
      input,
    );
  },
  /**
   * Upload de PDF pra criar uma KB de PRODUTO: o backend parseia o texto e a IA
   * extrai os campos da oferta (nome, preço, promessa, personas...). Bloqueante,
   * ~8-25s.
   */
  async extractProductPdf(
    companyId: string,
    file: File,
    options?: { name?: string },
  ): Promise<KnowledgeBase> {
    const formData = new FormData();
    formData.append("file", file);
    if (options?.name) formData.append("name", options.name);

    const res = await fetch(
      `/api/companies/${companyId}/knowledge/product/extract-pdf`,
      { method: "POST", credentials: "include", body: formData },
    );
    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      const msg =
        (payload as { error?: string } | null)?.error ?? `Extração falhou: ${res.status}`;
      throw new Error(msg);
    }
    return res.json();
  },
  /**
   * Upload de PDF/CSV/TXT pra criar uma KB de material.
   * Backend roda análise Haiku síncrona, popula nome/descrição/summary.
   * Pode demorar 8-25s.
   */
  async uploadMaterial(
    companyId: string,
    file: File,
    options?: { description?: string },
  ): Promise<KnowledgeBase> {
    const formData = new FormData();
    formData.append("file", file);
    if (options?.description) formData.append("description", options.description);

    const res = await fetch(
      `/api${`/companies/${companyId}/knowledge/upload`}`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      },
    );
    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      const msg =
        (payload as { error?: string } | null)?.error ?? `Upload failed: ${res.status}`;
      throw new Error(msg);
    }
    return res.json();
  },
};
